// Story 8.8 (ADO-2017), AC1/AC3: Free delivery progress: how much more the store needs
// to spend for free delivery, or that it has qualified. Advisory only; it never blocks
// sending (AC2).
//
// Usage (mounted by the cart review page, `CartPage`, and the cart side panel's sticky
// footer, `CartSidePanel`):
//
//   const status = useFreeDeliveryStatus(depotId);          // apps/web/src/basket
//   <FreeDeliveryProgress status={status} variant="review" />  // cart review, under the title
//   <FreeDeliveryProgress status={status} variant="compact" /> // cart side panel footer
//
// Props:
//   - `status`: the hook's value. `null` (loading, failed, no depot) and `{ state: 'NONE' }`
//     (the depot has no threshold, AC3) both render NOTHING: no wrapper, no heading, no
//     reserved space.
//   - `variant`: `review` (Figma 5006:3267 below / 5070:6251 met; on phones the heading
//     sits over the figures, 5324:13260) or `compact` (5009:3121; the met compact state is
//     not drawn, so it reuses the review met copy). Neither ever clips an amount: where
//     the row is too narrow, the heading wraps and the figures keep their full width.
//
// Copy follows Figma except that the em dash in the review helper line is replaced by a
// full stop (repo prose rule, D-10). Amounts go through the shared `formatMoney`, so the
// currency is whatever the API sent. a11y: a `progressbar` whose `aria-valuetext` carries
// the same content shown on screen (compact speaks "of" where the screen shows "/"), so
// the state is carried by text, never by the bar colour alone.

import { formatMoney, type MoneyValue } from '../../shared/money';
import styles from './FreeDeliveryProgress.module.css';

/** The free-delivery status, structurally the same as the basket client's union. */
export type FreeDeliveryProgressStatus =
  | { readonly state: 'NONE' }
  | {
      readonly state: 'BELOW';
      readonly threshold: MoneyValue;
      readonly spend: MoneyValue;
      readonly shortfall: MoneyValue;
    }
  | { readonly state: 'MET'; readonly threshold: MoneyValue; readonly spend: MoneyValue };

export type FreeDeliveryProgressVariant = 'review' | 'compact';

export interface FreeDeliveryProgressProps {
  readonly status: FreeDeliveryProgressStatus | null;
  readonly variant?: FreeDeliveryProgressVariant;
}

/** The reserved E2E hook (`TestIds.movIndicator` in apps/web-e2e). */
export const FREE_DELIVERY_PROGRESS_TEST_ID = 'mov-indicator';

/** Fill width as a percentage of the track, clamped to [0, 100]. */
function fillPercent(spend: number, threshold: number): number {
  if (!(threshold > 0)) return 100;
  return Math.min(100, Math.max(0, (spend / threshold) * 100));
}

export function FreeDeliveryProgress({ status, variant = 'review' }: FreeDeliveryProgressProps) {
  if (status === null || status.state === 'NONE') return null;

  const met = status.state === 'MET';
  const spend = formatMoney(status.spend);
  const threshold = formatMoney(status.threshold);
  const shortfall = status.state === 'BELOW' ? formatMoney(status.shortfall) : '';

  const heading = met
    ? 'Free delivery qualified'
    : variant === 'compact'
      ? `Add ${shortfall} for free delivery`
      : 'Minimum order value for free delivery';
  const helper =
    variant === 'compact'
      ? null
      : met
        ? 'This order ships free.'
        : `Add ${shortfall} more for free delivery. You can still send without it.`;
  // Same content as what's on screen either way: compact shows `{spend} / {threshold}` and
  // speaks "of" instead of "/" (the better spoken phrasing for a fraction-shaped value).
  const valueText =
    variant === 'compact'
      ? `${heading}. ${spend} of ${threshold}.`
      : met
        ? `${heading}. ${helper ?? ''}`
        : (helper ?? heading);

  const bar = (
    <div
      className={styles.track}
      role="progressbar"
      aria-label="Free delivery progress"
      aria-valuemin={0}
      aria-valuemax={status.threshold.amount}
      aria-valuenow={Math.min(status.spend.amount, status.threshold.amount)}
      aria-valuetext={valueText}
    >
      <div
        className={`${styles.fill} ${met ? styles.met : styles.below}`}
        style={{ width: `${fillPercent(status.spend.amount, status.threshold.amount)}%` }}
      />
    </div>
  );

  return (
    <div
      className={`${styles.root} ${styles[variant]}`}
      data-testid={FREE_DELIVERY_PROGRESS_TEST_ID}
      data-state={status.state}
    >
      {variant === 'compact' ? (
        <p className={styles.summary}>
          <span className={styles.label}>{heading}</span>
          <span className={styles.muted}>
            {spend} / {threshold}
          </span>
        </p>
      ) : (
        <p className={styles.summary}>
          <span className={styles.label}>{heading}</span>
          <span className={styles.figures} data-part="figures">
            <span className={styles.spend}>{spend}</span>
            <span className={styles.muted}>/ {threshold}</span>
          </span>
        </p>
      )}
      {bar}
      {helper === null ? null : <p className={styles.helper}>{helper}</p>}
    </div>
  );
}
